
import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { sanitizeContent, validateComment } from '@/utils/sanitization';

export interface DatabaseComment {
  id: string;
  created_at: string;
  content: string;
  story_id: string;
  user_id: string;
  author_name: string;
  author_avatar?: string;
  likes: number;
}

export const useComments = (storyId: string, user: User | null) => {
  const [comments, setComments] = useState<DatabaseComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [userLikes, setUserLikes] = useState<string[]>([]);

  // Fetch user likes when user is authenticated
  useEffect(() => {
    if (user) {
      fetchUserLikes(user.id);
    } else {
      setUserLikes([]);
    }
  }, [user]);
  
  const fetchUserLikes = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('comment_likes')
        .select('comment_id')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching user likes:', error);
      } else if (data) {
        setUserLikes(data.map(like => like.comment_id));
      }
    } catch (err) {
      console.error('Exception fetching user likes:', err);
    }
  };
  
  // Load comments from Supabase
  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('comments')
          .select('*')
          .eq('story_id', storyId)
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching comments:', error);
          toast.error('Failed to load comments');
        } else {
          setComments(data || []);
        }
      } catch (err) {
        console.error('Exception fetching comments:', err);
        toast.error('Failed to load comments');
      }
      setLoading(false);
    };
    
    fetchComments();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('comments-channel')
      .on(
        'postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'comments',
          filter: `story_id=eq.${storyId}`
        }, 
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setComments(prev => [payload.new as DatabaseComment, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setComments(prev => 
              prev.map(comment => 
                comment.id === payload.new.id ? payload.new as DatabaseComment : comment
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setComments(prev => 
              prev.filter(comment => comment.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [storyId]);
  
  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please log in to comment");
      return;
    }

    // Validate comment content
    const validation = validateComment(comment);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    // Sanitize content before storing
    const sanitizedContent = sanitizeContent(comment.trim());
    
    try {
      const { error } = await supabase
        .from('comments')
        .insert([
          { 
            content: sanitizedContent, 
            story_id: storyId,
            user_id: user.id,
            author_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Anonymous',
            author_avatar: user.user_metadata?.avatar_url
          }
        ]);
        
      if (error) {
        toast.error('Failed to post comment');
      } else {
        toast.success("Comment posted successfully!");
        setComment('');
      }
    } catch (err) {
      toast.error('Failed to post comment');
    }
  };
  
  const handleLikeComment = async (commentId: string) => {
    if (!user) {
      toast.error("Please log in to like comments");
      return;
    }
    
    const isAlreadyLiked = userLikes.includes(commentId);
    
    try {
      if (isAlreadyLiked) {
        // Unlike the comment
        const { error: unlikeError } = await supabase
          .from('comment_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('comment_id', commentId);
          
        if (unlikeError) {
          console.error('Error unliking comment:', unlikeError);
          toast.error('Failed to unlike comment');
          return;
        }
        
        // Update userLikes state
        setUserLikes(prev => prev.filter(id => id !== commentId));
        
        // Decrement likes count in comments table
        const { error: updateError } = await supabase
          .rpc('decrement_comment_likes', { comment_id: commentId });
          
        if (updateError) {
          console.error('Error updating like count:', updateError);
        }
      } else {
        // Like the comment
        const { error: likeError } = await supabase
          .from('comment_likes')
          .insert([
            { user_id: user.id, comment_id: commentId }
          ]);
          
        if (likeError) {
          console.error('Error liking comment:', likeError);
          toast.error('Failed to like comment');
          return;
        }
        
        // Update userLikes state
        setUserLikes(prev => [...prev, commentId]);
        
        // Increment likes count in comments table
        const { error: updateError } = await supabase
          .rpc('increment_comment_likes', { comment_id: commentId });
          
        if (updateError) {
          console.error('Error updating like count:', updateError);
        }
      }
    } catch (err) {
      console.error('Error handling like/unlike:', err);
      toast.error('Failed to process your like/unlike');
    }
  };

  return {
    comments,
    loading,
    comment,
    setComment,
    userLikes,
    handleCommentSubmit,
    handleLikeComment
  };
};
